import dayjs from 'dayjs';
import { stringify } from 'query-string';

import { ESearchFilters } from '@/constants/searchFilters';
import type { IBid } from '@/services/types/bids/bids';

// Mirrors odc-search's totalPriceScript: day-priced rent over nights + 1, fees and insurance.
const GUEST_FEE_RATE = 0.1;
const PREP_FEE_ALLOWANCE = 15_000;
const DAILY_INSURANCE_ALLOWANCE = 5_000;

// The API sends a zero date as "0001-01-01", and past dates would open an empty search.
const hasUpcomingDates = (bid: IBid) =>
  !!bid.from && !!bid.to && !dayjs(bid.from).isBefore(dayjs(), 'day');

const getTripFilters = (bid: IBid) => {
  if (!hasUpcomingDates(bid)) return {};

  const nights = Math.max(dayjs(bid.to).diff(dayjs(bid.from), 'day'), 1);
  const days = nights + 1;

  return {
    [ESearchFilters.DATE_FROM]: bid.from,
    [ESearchFilters.DATE_TO]: bid.to,
    [ESearchFilters.PRICE_MIN]: bid.min_day_price ? bid.min_day_price * nights : undefined,
    [ESearchFilters.PRICE_MAX]: bid.max_day_price
      ? Math.ceil(
          (bid.max_day_price * days + PREP_FEE_ALLOWANCE) * (1 + GUEST_FEE_RATE) +
            DAILY_INSURANCE_ALLOWANCE * days,
        )
      : undefined,
  };
};

// The empty state is shared by every unmatched bid, so it searches for the soonest upcoming trip.
export const getSimilarSearchBid = (bids: IBid[]) =>
  [...bids].sort((a, b) => {
    if (hasUpcomingDates(a) !== hasUpcomingDates(b)) return hasUpcomingDates(a) ? -1 : 1;
    return (a.from ?? '').localeCompare(b.from ?? '');
  })[0];

// The API sends zero for anything the Guest left out; bid prices are cents per day.
export const getBidSearchHref = (bid: IBid) =>
  `/rv-search?${stringify({
    [ESearchFilters.ADDRESS]: bid.pickup_location || undefined,
    [ESearchFilters.NEAR]:
      bid.pickup_lat && bid.pickup_lng ? `${bid.pickup_lat},${bid.pickup_lng}` : undefined,
    [ESearchFilters.FILTER_TYPE]: bid.type || undefined,
    [ESearchFilters.SLEEPS_ADULTS]: bid.travelers || undefined,
    ...getTripFilters(bid),
  })}`;
